import React, { useRef, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { CalendarIcon, Loader2, CheckCircle2, XCircle } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { format } from 'date-fns';
import { cn, formatDateForDB, parseDateFromDB } from '@/lib/utils';
import { ReceiptFilePicker } from './ReceiptFilePicker';
import { ParticipantsSelect } from './ParticipantsSelect';
import { Expenses, Receipts, ApiError } from '@/integrations/api';
import type { ExpenseType, PaymentMethod } from '@/integrations/api';
// Firmakort/betalingsmetode-valg midlertidigt fjernet (se Paid by-blokken nedenfor).
// import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { EXPENSE_TYPE_OPTIONS } from '@/lib/expenseOptions';
import { useAuth } from '@/hooks/useAuth';
import { toast } from '@/components/ui/use-toast';

interface AddExpenseModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit?: () => void; // Changed to optional callback for refresh
}

export function AddExpenseModal({ open, onClose, onSubmit }: AddExpenseModalProps) {
  const { user } = useAuth();
  const myInitials = user?.email ? user.email.split('@')[0].toUpperCase() : '';
  const [formData, setFormData] = useState({
    amount: '',
    description: '',
    expenseType: 'frokost' as ExpenseType,
    paymentMethod: 'privat' as PaymentMethod,
    receiptDate: new Date(),
    receiptFile: null as File | null,
    currency: 'DKK',
    participants: (myInitials ? [myInitials] : []) as string[]
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [ocrSuggesting, setOcrSuggesting] = useState(false);
  // Result of the two OCR analyses (amount + date), shown once suggestFieldsFromReceipt
  // finishes — null while no photo has been analyzed yet.
  const [ocrSummary, setOcrSummary] = useState<{
    amountFound: boolean;
    amount: number | null;
    dateFound: boolean;
    receiptDate: string | null;
    processingTimeMs: number;
  } | null>(null);
  // A ref (not state) so the OCR response handler always reads the CURRENT value, even if
  // the user opens the date picker while the (slow) OCR request is still in flight —
  // checking a captured state value here could otherwise use a stale "not touched yet".
  const dateTouchedRef = useRef(false);

  // OCR-suggest the amount/date from a receipt photo — a hint, never authoritative. Only
  // fills in fields the user hasn't already touched, and re-checks that guard when the
  // response actually arrives (via setFormData's updater, which always sees the latest
  // state), not just when the request was fired — so typing the amount by hand while OCR
  // is still running never gets overwritten.
  const suggestFieldsFromReceipt = async (file: File) => {
    setOcrSuggesting(true);
    setOcrSummary(null);
    try {
      const suggestion = await Receipts.extractFields(file);
      // Kept permanently at the user's request for ongoing console-side analysis of real
      // receipts. decode_ms + do_ocr_ms is what the response would have taken WITHOUT the
      // (slower) confidence pass broken out as get_words_ms.
      console.log(
        `[OCR] total ${suggestion.processing_time_ms}ms`
        + ` (decode ${suggestion.decode_ms}ms, doOCR ${suggestion.do_ocr_ms}ms, getWords ${suggestion.get_words_ms}ms`
        + ` — without confidence pass: ${suggestion.decode_ms + suggestion.do_ocr_ms}ms)`
        + ` — image ${suggestion.image_width}x${suggestion.image_height}`
        + ` — mean confidence: ${suggestion.mean_confidence?.toFixed(1) ?? 'n/a'}`
        + ` — amount: ${suggestion.amount_found ? suggestion.amount : 'not found'}`
        + `, date: ${suggestion.date_found ? suggestion.receipt_date : 'not found'}`
      );
      console.log('[OCR] raw text:', suggestion.raw_text);
      console.table(suggestion.word_confidences);
      setOcrSummary({
        amountFound: suggestion.amount_found,
        amount: suggestion.amount,
        dateFound: suggestion.date_found,
        receiptDate: suggestion.receipt_date,
        processingTimeMs: suggestion.processing_time_ms,
      });
      setFormData(prev => ({
        ...prev,
        amount: suggestion.amount_found && prev.amount === '' ? String(suggestion.amount) : prev.amount,
        receiptDate: suggestion.date_found && suggestion.receipt_date && !dateTouchedRef.current
          ? parseDateFromDB(suggestion.receipt_date)
          : prev.receiptDate,
      }));
    } catch {
      // Best-effort only — a failed suggestion just means the user fills the fields in
      // themselves, same as before this feature existed. Not worth interrupting them.
    } finally {
      setOcrSuggesting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      amount: '',
      description: '',
      expenseType: 'frokost',
      paymentMethod: 'privat',
      receiptDate: new Date(),
      receiptFile: null,
      currency: 'DKK',
      participants: myInitials ? [myInitials] : []
    });
    dateTouchedRef.current = false;
    setOcrSummary(null);
  };

  // Closing without submitting (Cancel, X, Esc, click outside) must reset just as much as a
  // successful submit does — otherwise stale data from an abandoned entry is still sitting
  // there the next time this modal opens.
  const handleClose = () => {
    resetForm();
    onClose();
  };

  const submit = async () => {
    if (!formData.amount || !user) {
      toast({
        title: "Error",
        description: "Please fill in all required fields",
        variant: "destructive"
      });
      return;
    }

    setIsSubmitting(true);

    try {
      let receiptUrl: string | null = null;

      // Upload receipt file if provided
      if (formData.receiptFile) {
        const { path } = await Receipts.upload(formData.receiptFile);
        receiptUrl = path;
      }

      const result = await Expenses.create({
        amount: parseFloat(formData.amount),
        description: formData.expenseType === 'andet' ? formData.description : '',
        expense_type: formData.expenseType,
        payment_method: formData.paymentMethod,
        receipt_date: formatDateForDB(formData.receiptDate),
        currency: formData.currency,
        receipt_url: receiptUrl,
        participants: formData.participants.join(', ')
      });

      // The expense is saved either way; warn only if the SharePoint backup was attempted
      // and failed, so the user knows to upload the receipt to SharePoint manually.
      if (result.sharepoint_attempted && !result.sharepoint_saved) {
        toast({
          title: "Saved — but not in SharePoint",
          description: "The expense was saved, but the receipt could not be backed up to SharePoint. Please upload the receipt to SharePoint manually.",
          variant: "destructive"
        });
      } else {
        toast({
          title: "Success",
          description: "Expense added successfully!"
        });
      }

      resetForm();
      onClose();
      onSubmit?.(); // Refresh the list
    } catch (error) {
      // Duplicate receipt (same user, same exact file): one physical file backs only one
      // expense, so we reject it and tell the user to pick a different file.
      if (error instanceof ApiError && error.status === 409) {
        toast({
          title: "Receipt already uploaded",
          description: "You've already uploaded this exact receipt. Pick a different file.",
          variant: "destructive"
        });
        return;
      }
      console.error('Error:', error);
      toast({
        title: "Error",
        description: "An unexpected error occurred",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    submit();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add New Expense</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Receipt Upload */}
          <div className="space-y-2">
            <Label>Receipt</Label>
            <ReceiptFilePicker
              file={formData.receiptFile}
              onSelect={(file) => {
                setFormData(prev => ({ ...prev, receiptFile: file }));
                if (file) {
                  suggestFieldsFromReceipt(file);
                } else {
                  setOcrSummary(null);
                }
              }}
            />
            {ocrSuggesting && (
              <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <Loader2 className="h-3 w-3 animate-spin" />
                Reading receipt…
              </p>
            )}
            {!ocrSuggesting && ocrSummary && (
              <div className="flex items-center gap-3 text-xs text-muted-foreground">
                <span className="flex items-center gap-1">
                  {ocrSummary.amountFound
                    ? <CheckCircle2 className="h-3 w-3 text-green-600" />
                    : <XCircle className="h-3 w-3" />}
                  {ocrSummary.amountFound ? `Amount: ${ocrSummary.amount}` : 'Amount not found'}
                </span>
                <span className="flex items-center gap-1">
                  {ocrSummary.dateFound
                    ? <CheckCircle2 className="h-3 w-3 text-green-600" />
                    : <XCircle className="h-3 w-3" />}
                  {ocrSummary.dateFound && ocrSummary.receiptDate
                    ? `Date: ${format(parseDateFromDB(ocrSummary.receiptDate), 'PPP')}`
                    : 'Date not found'}
                </span>
                <span>({(ocrSummary.processingTimeMs / 1000).toFixed(1)}s)</span>
              </div>
            )}
          </div>

          {/* Amount and Currency */}
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-2">
              <Label htmlFor="amount">Amount *</Label>
              <Input
                id="amount"
                type="number"
                step="0.01"
                placeholder="0.00"
                value={formData.amount}
                onChange={(e) => setFormData(prev => ({ ...prev, amount: e.target.value }))}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="currency">Currency</Label>
              <Select value={formData.currency} onValueChange={(value) => setFormData(prev => ({ ...prev, currency: value }))}>
                <SelectTrigger id="currency">
                  <SelectValue placeholder="Select currency" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="DKK">DKK</SelectItem>
                  <SelectItem value="EUR">EUR</SelectItem>
                  <SelectItem value="USD">USD</SelectItem>
                  <SelectItem value="GBP">GBP</SelectItem>
                  <SelectItem value="SEK">SEK</SelectItem>
                  <SelectItem value="NOK">NOK</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Type + Receipt Date side by side */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="expenseType">Type *</Label>
              <Select
                value={formData.expenseType}
                onValueChange={(value) => setFormData(prev => ({ ...prev, expenseType: value as ExpenseType }))}
              >
                <SelectTrigger id="expenseType">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  {EXPENSE_TYPE_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Receipt Date *</Label>
              <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !formData.receiptDate && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {formData.receiptDate ? format(formData.receiptDate, "PPP") : <span>Pick a date</span>}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={formData.receiptDate}
                    onSelect={(date) => {
                      if (date) {
                        dateTouchedRef.current = true;
                        setFormData(prev => ({ ...prev, receiptDate: date }));
                        setDatePickerOpen(false);
                      }
                    }}
                    initialFocus
                    className={cn("p-3 pointer-events-auto")}
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>

          {/* Betalingsmetode-valg (privat/firmakort) midlertidigt fjernet fra appen.
              Alt bogføres som "privat" (jf. formData.paymentMethod) indtil firmakort-
              beregningerne er på plads. Behold blokken så valget kan tændes igen.
          <div className="space-y-2">
            <Label>Paid by</Label>
            <RadioGroup
              value={formData.paymentMethod}
              onValueChange={(value) => setFormData(prev => ({ ...prev, paymentMethod: value as PaymentMethod }))}
              className="flex gap-4 pt-1"
            >
              {PAYMENT_METHOD_OPTIONS.map((opt) => (
                <div key={opt.value} className="flex items-center space-x-2">
                  <RadioGroupItem value={opt.value} id={`pay-${opt.value}`} />
                  <Label htmlFor={`pay-${opt.value}`} className="font-normal cursor-pointer">{opt.label}</Label>
                </div>
              ))}
            </RadioGroup>
          </div>
          */}

          {/* Participants */}
          <div className="space-y-2">
            <Label>Participants</Label>
            <ParticipantsSelect
              value={formData.participants}
              onChange={(participants) => setFormData(prev => ({ ...prev, participants }))}
            />
          </div>

          {/* Description — only shown for "Andet" */}
          {formData.expenseType === 'andet' && (
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                placeholder="Additional details about this expense"
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              />
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-2 pt-4 sticky bottom-0 bg-background border-t -mx-6 px-6 pb-1">
            <Button type="button" variant="outline" onClick={handleClose} className="flex-1">
              Cancel
            </Button>
            <Button type="submit" className="flex-1" disabled={isSubmitting}>
              {isSubmitting ? 'Adding...' : 'Add Expense'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}